import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export const ImagePickerField = ({
  label,
  value,
  onChange,
  values,
  onValuesChange,
  multiple = false,
  dataTestId,
  buttonLabel = "Bild auswählen",
}) => {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const selectedValues = useMemo(() => (multiple ? values || [] : value ? [value] : []), [multiple, value, values]);

  useEffect(() => {
    if (!open) return;
    const loadImages = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/images`, auth());
        setImages(res.data || []);
      } catch {
        toast.error("Bilder konnten nicht geladen werden");
      }
      setLoading(false);
    };
    loadImages();
  }, [open]);

  const selectImage = (image) => {
    const imageUrl = `${API}/images/${image.id}/data`;
    if (multiple) {
      if (!selectedValues.includes(imageUrl)) onValuesChange([...(values || []), imageUrl]);
    } else {
      onChange(imageUrl);
    }
    setOpen(false);
  };

  const removeImage = (imageUrl) => {
    if (multiple) onValuesChange((values || []).filter((item) => item !== imageUrl));
    else onChange("");
  };

  return (
    <div className="mb-4">
      {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
        <div className="flex flex-wrap gap-3 mb-3">
          {selectedValues.length > 0 ? selectedValues.map((imageUrl, index) => (
            <div key={`${imageUrl}-${index}`} className="relative w-24 h-24 rounded-lg overflow-hidden border bg-white">
              <img src={imageUrl} alt="Ausgewählt" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(imageUrl)}
                data-testid={`${dataTestId}-remove-${index}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white text-xs"
              >
                ×
              </button>
            </div>
          )) : <p className="text-sm text-gray-500">Kein Bild ausgewählt</p>}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`${dataTestId}-open-button`}
          className="px-4 py-2 rounded-lg bg-[#0A2A43] text-white text-sm hover:bg-[#0A2A43]/90"
        >
          {buttonLabel}
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid={`${dataTestId}-modal`}>
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl border overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="font-semibold text-[#0A2A43]">Bild auswählen</h3>
                <p className="text-sm text-gray-500">Vorhandene Bilder aus der Bilderverwaltung</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-900" data-testid={`${dataTestId}-close-button`}>
                Schließen
              </button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {loading ? (
                <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div>
              ) : images.length === 0 ? (
                <p className="text-gray-500 text-center py-12">Keine Bilder vorhanden</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map((image, index) => {
                    const imageUrl = `${API}/images/${image.id}/data`;
                    const selected = selectedValues.includes(imageUrl);
                    return (
                      <button
                        key={image.id}
                        type="button"
                        onClick={() => selectImage(image)}
                        data-testid={`${dataTestId}-image-${index}`}
                        className={`rounded-xl overflow-hidden border text-left transition ${selected ? "border-[#D32F2F] ring-2 ring-[#D32F2F]/30" : "border-gray-200 hover:border-[#0A2A43]"}`}
                      >
                        <div className="aspect-square bg-gray-100">
                          <img src={imageUrl} alt={image.original_name} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-2 bg-white">
                          <p className="text-xs text-[#0A2A43] truncate">{image.original_name}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
